import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface PageBoundary {
  /** Document position of the block that the page begins at. */
  pos: number;
  page: number;
}

export interface PageGuideState {
  decorations: DecorationSet;
  /** Where the render started each page, or null while the renderer has not answered. */
  boundaries: RenderedBoundary[] | null;
}

const pageGuideKey = new PluginKey<PageGuideState>('pageGuide');
const pageBreaksKey = new PluginKey('pageGuideBreaks');

/** A page the render started, identified by the text the page opens with. */
export interface RenderedBoundary {
  page: number;
  anchor: string;
  /**
   * Which occurrence of the anchor the render meant, counted from the start of the document.
   * A render that reports it pins the boundary exactly; without it the only disambiguation
   * left is that pages run in order.
   */
  occurrence?: number;
}

/**
 * The document's text with a position for every character, whitespace collapsed the way the
 * server collapses it so the two sides compare the same string.
 */
export const collapsedTextIndex = (doc: ProseMirrorNode) => {
  let text = '';
  const positions: number[] = [];
  let pendingSpace = false;

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      // Blocks are separated by no character at all, but the server writes one between them.
      pendingSpace = text.length > 0;
      return true;
    }

    for (let i = 0; i < node.text.length; i += 1) {
      const char = node.text[i];
      if (/\s/.test(char)) {
        pendingSpace = text.length > 0;
        continue;
      }
      if (pendingSpace) {
        text += ' ';
        positions.push(pos + i);
        pendingSpace = false;
      }
      text += char;
      positions.push(pos + i);
    }
    // The server writes a space after every text node, so text split by a mark boundary
    // mid-word ("$369,230" beside a superscript) reads as two words on its side and one here.
    pendingSpace = text.length > 0;
    return true;
  });

  return { text, positions };
};

/**
 * Boundaries taken from a rendered page map.
 *
 * The render reports where each page began as a sample of that page's opening text, not as a
 * block number. Block numbers cannot survive the trip — the server counts blocks in the
 * stored markup while the editor counts nodes the schema produced — and text also reaches
 * places a block number cannot, because a page is free to begin part-way through a paragraph.
 *
 * A match that lands on a block's first character snaps to the block itself, so an ordinary
 * boundary still sits cleanly between two blocks rather than inside the second one.
 *
 * Every boundary resolves strictly after the one before it, which is also what keeps a gutter
 * from being drawn above the first block. Forward matching satisfies that on its own, so
 * repeated wording lands on successive pages; an ordinal does not, so a numbered boundary that
 * contradicts the one before it is dropped rather than drawn in the wrong place.
 */
export const boundariesFromAnchors = (
  doc: ProseMirrorNode,
  rendered: RenderedBoundary[]
): PageBoundary[] => {
  const index = collapsedTextIndex(doc);

  const boundaries: PageBoundary[] = [];
  let from = 0;
  let last = 0;

  for (const { page, anchor, occurrence } of rendered) {
    if (!anchor) continue;

    const match = matchAnchor(doc, index, { anchor, occurrence, from, after: last });
    if (!match) continue;

    from = match.at + 1;
    last = match.pos;
    boundaries.push({ pos: match.pos, page });
  }

  return boundaries;
};

/**
 * The occurrence of an anchor a boundary means: its index in the document text and the
 * document position it snaps to, or null when nothing usable follows `after`.
 */
const matchAnchor = (
  doc: ProseMirrorNode,
  { text, positions }: ReturnType<typeof collapsedTextIndex>,
  {
    anchor,
    occurrence,
    from,
    after
  }: {
    anchor: string;
    occurrence: number | undefined;
    from: number;
    after: number;
  }
): { at: number; pos: number } | null => {
  if (occurrence !== undefined) {
    const at = nthOccurrence(text, anchor, occurrence);
    if (at < 0) return null;
    const pos = snapToBlockStart(doc, positions[at]);
    // An ordinal names one occurrence, so a boundary that contradicts the one before it is bad
    // data rather than a near miss worth scanning past.
    return pos > after ? { at, pos } : null;
  }

  for (let at = text.indexOf(anchor, from); at >= 0; at = text.indexOf(anchor, at + 1)) {
    const pos = snapToBlockStart(doc, positions[at]);
    if (pos > after) return { at, pos };
  }
  return null;
};

const nthOccurrence = (text: string, anchor: string, occurrence: number): number => {
  let at = text.indexOf(anchor);
  for (let seen = 0; seen < occurrence && at >= 0; seen += 1) {
    at = text.indexOf(anchor, at + 1);
  }
  return at;
};

/**
 * Walks a match outwards while it sits at the very start of what encloses it.
 *
 * A page beginning at a heading inside a bordered box begins at the box, not at the heading —
 * stopping at the heading draws the gutter between the box's top edge and its first line.
 */
const snapToBlockStart = (doc: ProseMirrorNode, pos: number) => {
  let resolved = doc.resolve(pos);
  while (resolved.depth > 0 && resolved.parentOffset === 0) {
    resolved = doc.resolve(resolved.before(resolved.depth));
  }
  return resolved.pos;
};

const DECLARED_PAGE = /(?:^|\s)cara-page(?:\s|$)/;

/** A page the document declares for itself, as `<section class="cara-page">`. */
export const isDeclaredPage = (node: ProseMirrorNode) =>
  node.type.name === 'section' && DECLARED_PAGE.test(String(node.attrs.class ?? ''));

/**
 * Where the declared page around a boundary ends, or null when no declared page encloses it. A
 * boundary at a page's start snaps to the section itself, so only one inside an overflowing page
 * has an enclosing section.
 */
const enclosingPageEnd = (doc: ProseMirrorNode, pos: number): number | null => {
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if (isDeclaredPage($pos.node(depth))) return $pos.end(depth);
  }
  return null;
};

const SPILL = { class: 'cara-page-spill' };

/**
 * Shades the content between two positions. Whole blocks take the class themselves, so a table
 * or a list shades as one block; only a paragraph the range cuts is shaded as text.
 */
const spillDecorations = (doc: ProseMirrorNode, from: number, to: number): Decoration[] => {
  const spill: Decoration[] = [];
  doc.nodesBetween(from, to, (node, pos) => {
    const end = pos + node.nodeSize;
    if (pos >= from && end <= to) {
      spill.push(Decoration.node(pos, end, SPILL));
      return false;
    }
    if (node.isTextblock) {
      const start = Math.max(from, pos + 1);
      const stop = Math.min(to, end - 1);
      if (start < stop) spill.push(Decoration.inline(start, stop, SPILL));
      return false;
    }
    return true;
  });
  return spill;
};

const guide = (printsOn: number | null) => {
  const marker = document.createElement('div');
  marker.className = 'cara-page-guide';
  marker.setAttribute('contenteditable', 'false');
  marker.setAttribute('aria-hidden', 'true');
  if (printsOn !== null) {
    const label = document.createElement('span');
    label.className = 'cara-page-guide-label';
    label.textContent = `Prints on page ${printsOn}`;
    marker.appendChild(label);
  }
  return marker;
};

/**
 * The gutter between two pages.
 *
 * In a document that declares no pages the gutter is the whole of the drawing. The page itself
 * is painted by the canvas behind the document, so a gutter is simply a gray band across it, and
 * the page that follows the last gutter runs on to the bottom of the canvas without anything
 * having to work out how much of it is left.
 *
 * A gutter inside a declared page is that page overflowing. It names the PDF page the rest of the
 * sheet prints on, and that rest, up to the next break or the sheet's end, is shaded.
 */
const buildDecorations = (doc: ProseMirrorNode, boundaries: PageBoundary[]): DecorationSet => {
  const decorations: Decoration[] = [];
  boundaries.forEach(({ pos, page }, index) => {
    const pageEnd = enclosingPageEnd(doc, pos);
    decorations.push(
      Decoration.widget(pos, () => guide(pageEnd === null ? null : page), {
        side: -1,
        key: `page-guide-${page}${pageEnd === null ? '' : '-spill'}`
      })
    );
    if (pageEnd === null) return;
    const next = boundaries[index + 1]?.pos ?? pageEnd;
    decorations.push(...spillDecorations(doc, pos, Math.min(next, pageEnd)));
  });
  return DecorationSet.create(doc, decorations);
};

/**
 * Hands the extension the page map from a probed render, or null to fall back to measuring.
 * Guides are rebuilt from the document, so this costs no layout work.
 */
export const setPageBreaks = (editor: Editor, rendered: RenderedBoundary[] | null) => {
  // The map can arrive before the view exists, and a destroyed editor keeps its reference.
  if (!editor.view || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(pageBreaksKey, rendered));
};

/**
 * The state a page map produces.
 *
 * A null map draws nothing. The renderer is the only thing that decides where pages break,
 * so when it has not answered there are no boundaries to show — an editor-side estimate
 * would be a second opinion from a layout the export never used.
 */
export const pageGuideStateFor = (
  rendered: RenderedBoundary[] | null,
  doc: ProseMirrorNode
): PageGuideState =>
  rendered
    ? {
        boundaries: rendered,
        decorations: buildDecorations(doc, boundariesFromAnchors(doc, rendered))
      }
    : { boundaries: null, decorations: DecorationSet.empty };

export const PageGuide = Extension.create({
  name: 'pageGuide',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pageGuideKey,
        state: {
          init: () => ({ decorations: DecorationSet.empty, boundaries: null }) as PageGuideState,
          apply(tr, value): PageGuideState {
            const rendered = tr.getMeta(pageBreaksKey) as RenderedBoundary[] | null | undefined;
            if (rendered !== undefined) {
              return pageGuideStateFor(rendered, tr.doc);
            }

            // setContent(..., { emitUpdate: false }) replaces the saved document when its
            // repaired HTML arrives. Old positions cannot survive that replacement; the
            // renderer's text anchors can. User edits still map the existing boundaries.
            if (tr.docChanged && tr.getMeta('preventUpdate')) {
              return pageGuideStateFor(value.boundaries, tr.doc);
            }

            return { ...value, decorations: value.decorations.map(tr.mapping, tr.doc) };
          }
        },
        props: {
          decorations(state) {
            return pageGuideKey.getState(state)?.decorations;
          }
        }
      })
    ];
  }
});
